/* A tree's field-audit state in words, as its row reads it and a send's report
 * names who holds a tree, and what tree detail shows of its audits (27 section
 * 8).
 */
import type { AuditState, FieldAudit, FieldAuditPhoto, Me, Person, SendResult, TreeDetailRecord } from "../api/types";
import { formatDateTime, plural } from "./format";

/* The holder's name, the caller's own included, so a line reads the same to
 * whoever opens it. */
export function holderName(holder: Person): string {
  return holder.name ?? "an unnamed account";
}

export function auditLabel(state: AuditState | null, heldBy: Person | null): string {
  switch (state) {
    case "sent":
      return heldBy ? `Sent for audit · ${holderName(heldBy)}` : "Sent for audit";
    case "validated":
      return "Validated";
    case "corrected":
      return "Corrected";
    case null:
      return "—";
  }
}

/* What a send did, in one line: how many it sent, then each tree it skipped,
 * by `name` of its id - a tree's or a missed tree's - with who holds it. */
export function sendReport(
  sent: number,
  skipped: SendResult["skipped"],
  name: (treeId: string) => string,
): string {
  const lead = `${plural(sent, "tree")} sent for audit.`;
  if (skipped.length === 0) return lead;
  const held = skipped
    .map((s) => `${name(s.tree_id !== null ? s.tree_id : s.missed_tree_id)} (${holderName(s.held_by)})`)
    .join(", ");
  return `${lead} Already out, so not sent: ${held}.`;
}

/* What the field says of one of Kanopy's values, for tree detail to show
 * beneath it. The newest audit decides: an audit records each value the
 * arborist changed, and a null agrees with the system (15 section 3.2), so the
 * newest one's own value where it changed this one, else null for agreement.
 * Undefined where no audit has synced. The audits are newest first, as the
 * tree's read serves them. */
export function fieldReading<K extends "species" | "clearance_m" | "encroachment_height_m">(
  audits: readonly FieldAudit[],
  key: K,
): { value: FieldAudit[K]; audit: FieldAudit } | undefined {
  const newest = audits[0];
  return newest === undefined ? undefined : { value: newest[key], audit: newest };
}

/* An audit as its photos are shown: one of a tree's, or the one that recorded
 * a missed tree, which its read serves apart from its later audits (15
 * section 3.16). */
export type PhotographedAudit = Pick<FieldAudit, "recorded_by" | "photos"> & { outcome: FieldAudit["outcome"] | "missed" };

/* A tree's audit photos as Audit imagery pages through them: the newest
 * audit's first, as tree detail lists its audits, and each audit's in the
 * order it took them - both as the tree's read serves them. */
export function auditPhotos<A extends PhotographedAudit>(audits: readonly A[]): { photo: FieldAuditPhoto; audit: A }[] {
  return audits.flatMap((audit) => audit.photos.map((photo) => ({ photo, audit })));
}

const PHOTO_OUTCOME: Record<PhotographedAudit["outcome"], string> = {
  validated: "Validated",
  corrected: "Corrected",
  missed: "Recorded",
};

/* Over the photo shown, as a frame's date and count sit over it: the audit
 * that took it, who, when, and where it falls among the tree's photos. */
export function auditPhotoCaption(
  audit: Pick<PhotographedAudit, "outcome" | "recorded_by">,
  photo: FieldAuditPhoto,
  index: number,
  count: number,
): string {
  return `${PHOTO_OUTCOME[audit.outcome]} by ${holderName(audit.recorded_by)} · ${formatDateTime(photo.taken_at)} · Photo ${index + 1} of ${count}`;
}

/* Whether tree detail offers Release from audit: while the tree is out, to
 * its holder while they may record field audits, or to an administrator. The
 * route also refuses an administrator whose membership is read-only, which
 * /api/me does not say, so that case is the route's to refuse (15 section
 * 3.15). */
export function mayReleaseFromAudit(
  tree: Pick<TreeDetailRecord, "open_assignment" | "held_by">,
  me: Pick<Me, "user_id" | "role" | "may_record_field_audits"> | undefined,
): boolean {
  if (tree.open_assignment === null || me === undefined) return false;
  const holds = tree.held_by !== null && tree.held_by.user_id === me.user_id;
  return (holds && me.may_record_field_audits) || me.role === "administrator";
}
